import { Vehicle } from '@/types';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableRow,
} from '@/components/ui/table';

interface TechnicalSpecsProps {
  vehicle: Vehicle;
}

export const TechnicalSpecs = ({ vehicle }: TechnicalSpecsProps) => {
  const specs = [
    { label: 'Marque', value: vehicle.brand },
    { label: 'Titre', value: vehicle.title },
    { label: 'Catégorie', value: getCategoryLabel(vehicle.category) },
    { label: 'Année', value: vehicle.year && vehicle.year !== 'avant-1990' ? vehicle.year : 'Avant 1990' },
    { label: 'Kilométrage', value: vehicle.mileage ? `${vehicle.mileage.toLocaleString('fr-FR')} km` : undefined },
    { label: 'Carburant', value: getFuelLabel(vehicle.fuelType) },
    { label: 'Transmission', value: getTransmissionLabel(vehicle.transmission) },
    { label: 'Puissance', value: vehicle.enginePower ? `${vehicle.enginePower} ch` : undefined },
    { label: 'Cylindrée', value: vehicle.engineDisplacement ? `${vehicle.engineDisplacement} L` : undefined },
    { label: 'Configuration essieux', value: vehicle.axleConfiguration },
    { label: 'PTAC', value: vehicle.maxWeight ? `${vehicle.maxWeight.toLocaleString('fr-FR')} kg` : undefined },
    { label: 'Charge utile', value: vehicle.payloadCapacity ? `${vehicle.payloadCapacity.toLocaleString('fr-FR')} kg` : undefined },
    { label: 'PTAV', value: vehicle.ptav ? `${vehicle.ptav.toLocaleString('fr-FR')} kg` : undefined },
    { label: 'PTRA', value: vehicle.ptra ? `${vehicle.ptra.toLocaleString('fr-FR')} kg` : undefined },
    { label: 'Empâtement', value: vehicle.wheelbase ? `${vehicle.wheelbase} mm` : undefined },
    { label: 'Norme Euro', value: vehicle.euroStandard },
    { label: 'Vignette Crit\'Air', value: vehicle.critAir ? (vehicle.critAir === 'exempt' ? 'Exempté' : `Crit'Air ${vehicle.critAir}`) : undefined },
    { label: 'Type de carrosserie', value: vehicle.bodyType },
    { label: 'Type de suspension', value: vehicle.suspensionType },
    { label: 'Direction assistée', value: vehicle.powerSteering ? 'Oui' : 'Non' },
    { label: 'Position de conduite', value: vehicle.drivingPosition },
    { label: 'Permis requis', value: vehicle.drivingLicenseRequired },
    { label: 'Numéro de série', value: vehicle.serialNumber },
  ].filter(spec => spec.value !== undefined && spec.value !== null && spec.value !== '');

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-2xl">Caractéristiques techniques</CardTitle>
      </CardHeader>
      <CardContent>
        <Table>
          <TableBody>
            {specs.map((spec, index) => (
              <TableRow key={index}>
                <TableCell className="font-semibold w-1/3 py-3">{spec.label}</TableCell>
                <TableCell className="py-3">{spec.value}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};

const getCategoryLabel = (category: Vehicle['category']): string => {
  const labels: Record<Vehicle['category'], string> = {
    'camion': 'Camion',
    'materiel-industriel': 'Matériel industriel',
    'utilitaire': 'Utilitaire',
    'remorque': 'Remorque',
    'autre': 'Autre',
  };
  return labels[category] || category;
};

const getFuelLabel = (fuel?: Vehicle['fuelType']): string | undefined => {
  if (!fuel) return undefined;
  const labels: Record<NonNullable<Vehicle['fuelType']>, string> = {
    'diesel': 'Diesel',
    'essence': 'Essence',
    'electrique': 'Électrique',
    'hybride': 'Hybride',
    'gaz': 'Gaz',
  };
  return labels[fuel];
};

const getTransmissionLabel = (transmission?: Vehicle['transmission']): string | undefined => {
  if (!transmission) return undefined;
  const labels: Record<NonNullable<Vehicle['transmission']>, string> = {
    'manuelle': 'Manuelle',
    'automatique': 'Automatique',
    'semi-automatique': 'Semi-automatique',
  };
  return labels[transmission];
};
